import * as _ from "lodash";
import { createContext, FC, ReactNode, useContext, useState } from "react";
import { Link } from "react-router-dom";

import {
  CtraLayout,
  Grid,
  Menu,
  Row,
  Skeleton,
  RightOutlined,
  List,
  GamePlan,
  Button,
  PlusOutlined
} from "@ctra/components";

import { Enterprise as Copy, useTranslation } from "@ctra/i18n";
import { useGoogleAnalytics } from "@ctra/analytics";
import { Enterprise } from "@ctra/api";

import { DataDictionaryProvider } from "@base";
import { useFarm } from "@farms";
import { Routes } from "@routes";

import { GAActions } from "../../analytics";
import { GamePlanListFilter, GamePlanListProvider } from "../../providers";
import { GamePlanEntry } from "../GamePlanEntry";
import styles from "./GamePlanList.module.less";

const { ContentWrapper, WidgetWrapper } = CtraLayout;

const {
  V2: {
    List: { Header, Empty }
  }
} = GamePlan;

/**
 * Based on the variant, the chart may appear
 * next or under the task list
 */
export enum GamePlanVariant {
  Narrow = "narrow",
  Wide = "wide"
}

/**
 * Make a context for the game plan layout variants
 */
export const GamePlanContext = createContext<{
  layout: GamePlanVariant;
}>({
  layout: GamePlanVariant.Wide
});

interface GamePlanListProps {
  /**
   * extra UI in the header (eg. "create game plan" button")
   */
  extra?: ReactNode;
  /**
   * hide filter (active/previous)
   */
  showFilter?: boolean;
}

/**
 * Column specs for the different types of game plan layouts
 */
export const GamePlanLayout = {
  title: 8,
  kpi: 4,
  current: 2,
  target: 2,
  tasks: 3,
  period: 4
};

/**
 * Game plan list for the given farm ID
 * @param extra
 * @param showFilter
 * @constructor
 */
export const GamePlanList: FC<GamePlanListProps> = ({ extra, showFilter = true }) => {
  const { md } = Grid.useBreakpoint();
  const { t } = useTranslation();
  const { trackEvent } = useGoogleAnalytics();
  const { farm } = useFarm();
  const [filter, setFilter] = useState<GamePlanListFilter>(GamePlanListFilter.active);
  const { layout } = useContext(GamePlanContext);
  const isWide = layout === GamePlanVariant.Wide;

  const defaultPageSize = isWide ? 10 : 3;
  const [pageSize, setPageSize] = useState<number>(defaultPageSize);

  const {
    gamePlan: {
      navigation: { active, previous },
      actions: { create },
      list: {
        headers,
        viewAll,
        empty: { title: emptyTitle, description: emptyDescription }
      }
    }
  } = Copy;

  /**
   * Get the column widths for the given setting
   */
  const { title, kpi, current, target, tasks, period } = GamePlanLayout;

  /**
   * Active/previous navigation
   * @todo make a TabNavigation component
   */
  const navigation = (
    <Menu
      className={styles.Navigation}
      selectedKeys={[filter]}
      mode="horizontal"
      onClick={(e) => {
        /* istanbul ignore next */
        trackEvent(e.key === "active" ? GAActions.goToActive : GAActions.goToPrevious);
        setFilter(e.key as GamePlanListFilter);
      }}
    >
      <Menu.Item key={GamePlanListFilter.active}>{t<string>(active)}</Menu.Item>
      <Menu.Item key={GamePlanListFilter.previous}>{t<string>(previous)}</Menu.Item>
    </Menu>
  );

  /**
   * Row with headers
   */
  const headRow =
    md && isWide ? (
      <Header
        labels={{
          objective: t<string>(headers.title),
          kpi: t<string>(headers.kpi),
          current: t<string>(headers.current),
          target: t<string>(headers.target),
          tasks: t<string>(headers.tasks),
          period: t<string>(headers.period)
        }}
        span={{
          objective: title,
          kpi,
          current,
          target,
          tasks,
          period
        }}
      />
    ) : null;

  return (
    <DataDictionaryProvider>
      {(dataDictionary, { isLoading: isDataDictionaryLoading }) => (
        <GamePlanListProvider filter={filter} farmID={farm?.id}>
          {(gamePlanList, { isLoading: isGamePlanListLoading }) => {
            const items = _.values(gamePlanList);
            const length = items.length;
            const paginationHidden = !(md || isWide) && length > defaultPageSize;

            return (
              <WidgetWrapper
                toolbar={
                  <Button
                    onClick={() => Enterprise.history.push(Routes.app.gamePlans.create.index)}
                    icon={<PlusOutlined />}
                    type="secondary"
                  >
                    {t<string>(create, { variant: null })}
                  </Button>
                }
              >
                {showFilter && navigation}
                <ContentWrapper padded={false} className={styles.ListWrapper}>
                  <Skeleton
                    loading={isDataDictionaryLoading || isGamePlanListLoading}
                    className={styles.Skeleton}
                  >
                    {_.isEmpty(gamePlanList) ? (
                      <Empty title={t<string>(emptyTitle)} description={t<string>(emptyDescription)} />
                    ) : (
                      <>
                        {headRow}
                        <List
                          className={paginationHidden ? styles.Hide : styles.Pagination}
                          dataSource={items}
                          renderItem={(gamePlan) => <GamePlanEntry key={gamePlan.id} {...gamePlan} />}
                          pagination={{
                            size: "small",
                            defaultPageSize,
                            pageSize,
                            total: length,
                            disabled: length <= defaultPageSize,
                            showSizeChanger: length > defaultPageSize,
                            pageSizeOptions: _.times(3, (i) => (defaultPageSize * (i + 1)).toString()),
                            onShowSizeChange: (current, pageSize) => setPageSize(pageSize)
                          }}
                        />
                        {paginationHidden && (
                          <Row justify="center" className={styles.All}>
                            <Link to={Routes.app.gamePlans.index}>
                              <Row align="middle">
                                {t<string>(viewAll)}&nbsp;
                                <RightOutlined />
                              </Row>
                            </Link>
                          </Row>
                        )}
                      </>
                    )}
                  </Skeleton>
                </ContentWrapper>
              </WidgetWrapper>
            );
          }}
        </GamePlanListProvider>
      )}
    </DataDictionaryProvider>
  );
};
